/**
 * Bảo trì TỪNG TÍNH NĂNG (BE migration v121) — khác màn bảo trì toàn hệ thống
 * (`maintenance-config.ts`): app vẫn chạy, chỉ trang của tính năng đang bảo trì hiện
 * banner, và gọi API của nó thì nhận 503 kèm lời nhắn (đã hiện qua toast như mọi lỗi).
 *
 * 🔴 Hỏi `ai-be` (`BOIDE_API_URL`), KHÔNG hỏi `account-be`: bảo trì từng tính năng chỉ
 * có nghĩa khi `ai-be` còn sống — nó chặn ở chính middleware của `ai-be`.
 *
 * 🔴 DANH MỤC trang ↔ tính năng (`catalog[].fe_paths`) đọc từ máy chủ, FE KHÔNG giữ
 * bản chép: thêm tính năng ở BE là banner tự có mà không phải deploy lại FE.
 *
 * ⚠️ Nuốt mọi lỗi, giữ nguyên trạng thái cũ — mất mạng không phải là bảo trì.
 */

import { BOIDE_API_URL } from "@/lib/boide-api-url";

export type FeatureMaintenance = {
  slug: string;
  label: string;
  message: string;
  eta: string;
  block_reads: boolean;
  started_at: string | null;
};

type CatalogItem = { slug: string; label: string; fe_paths: string[] };

type State = {
  features: Record<string, FeatureMaintenance>;
  catalog: CatalogItem[];
  /** Người đang xem được BE cho đi qua (tài khoản admin / IP được tha) — KHÔNG khoá trang. */
  bypass: boolean;
};

let current: State = { features: {}, catalog: [], bypass: false };

// Token đăng nhập đọc qua getter do nơi mount truyền vào, KHÔNG import `boide-api`:
// file đó import ngược file này (`noteFeatureMaintenance` trong `unwrap`).
let tokenGetter: () => string = () => "";
export function setFeatureMaintenanceTokenGetter(getter: () => string): void {
  tokenGetter = getter;
}
const listeners = new Set<() => void>();

export function subscribeFeatureMaintenance(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getFeatureMaintenance(): State {
  return current;
}

let inFlight: Promise<State> | null = null;

export function loadFeatureMaintenance(): Promise<State> {
  if (inFlight) return inFlight;
  const token = tokenGetter();
  inFlight = fetch(`${BOIDE_API_URL}/v1/maintenance/features`, {
    // Kèm token để BE biết người hỏi có phải admin không (→ `bypass`).
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((json) => {
      const data = json?.data;
      if (data && typeof data.features === "object" && Array.isArray(data.catalog)) {
        const next: State = {
          features: data.features ?? {},
          catalog: data.catalog,
          bypass: Boolean(data.bypass),
        };
        if (JSON.stringify(next) !== JSON.stringify(current)) {
          current = next;
          for (const listener of listeners) listener();
        }
      }
      return current;
    })
    .catch(() => current)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

let lastProbe = 0;

/** Gọi khi một lượt API trả 503 kèm `feature_maintenance` — tải lại để banner hiện ngay. */
export function noteFeatureMaintenance(): void {
  const now = Date.now();
  if (now - lastProbe < 10_000) return;
  lastProbe = now;
  void loadFeatureMaintenance();
}

/**
 * Tính năng sở hữu một trang. "/x" = đúng trang đó, "/x/*" = mọi trang con.
 * Khớp đúng trang thắng khớp tiền tố, tiền tố dài thắng tiền tố ngắn — nên
 * "/tong-hop/van-khi" (Vận khí) thắng "/tong-hop/*" (Bói Đê) và "/tong-hop" là chat.
 */
export function featureForPath(pathname: string, catalog: CatalogItem[]): string | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  let best: string | null = null;
  let bestScore = -1;
  for (const item of catalog) {
    for (const pattern of item.fe_paths) {
      let score = -1;
      if (pattern.endsWith("/*")) {
        const base = pattern.slice(0, -2);
        if (path.startsWith(base + "/")) score = base.length;
      } else if (path === pattern) {
        score = 10_000 + pattern.length;
      }
      if (score > bestScore) {
        bestScore = score;
        best = item.slug;
      }
    }
  }
  return best;
}
